/**
 * Type Compatibility Analysis for Change Type feature
 *
 * Analyzes which types an individual can be changed to while preserving
 * all existing relationships. Uses fixed ceiling nodes for main categories
 * (Building, Level, Room, Equipment, System, RealEstate, Point) to determine
 * which types are valid alternatives.
 */

import { Iris } from '@/rec-cache/rec-cache-types'

/**
 * Fixed ceiling types - the main categories that define type change boundaries.
 * Users can change types within these categories but not across them.
 *
 * Used only when the ontology cannot say where the boundaries are. The next
 * engine's ontology answers `boundaryOf`, from whatever schema declared the
 * boundary — so a type a customer uploads is bounded without this list
 * being edited. 0.9.x's ontology has no such method and falls back to here.
 */
const CEILING_TYPE_IRIS = [
  Iris.recBuilding,
  Iris.recLevel,
  Iris.recRoom,
  Iris.brickEquipment,
  Iris.brickSystem,
  Iris.recRealEstate,
  Iris.brickPoint,
]

/**
 * Get all types within the same hierarchy that are compatible with
 * the individual's current relationships.
 *
 * Algorithm:
 * 1. Find the fixed ceiling type (Building, Level, Room, Equipment, System, RealEstate)
 *    that is an ancestor of the current type
 * 2. All subclasses of that ceiling are valid new types
 *
 * @param {RecIndividual} individual - The individual to analyze
 * @param {RecCache} cache - The cache for querying edges
 * @param {Ontology} ontology - The ontology service
 * @returns {Object} - Result object with compatible types and metadata
 */
export function getCompatibleTypesInHierarchy(individual, cache, ontology) {
  const currentType = individual.type

  if (!currentType?.isDefined) {
    return {
      root: null,
      currentType: null,
      compatibleTypes: [],
      hierarchyTypes: [],
      hasCompatibleTypes: false,
      error: 'Individual has no defined type',
    }
  }

  // Get current relationships
  const outgoingEdges = cache.queryEdges({ source: individual.id.value })
  const incomingEdges = cache.queryEdges({ target: individual.id.value })

  // Get superclass chain (ordered from root to immediate parent)
  const superclasses = ontology.superclasses(currentType) || []

  // Build full type chain (current type + all ancestors)
  const typeChain = [currentType, ...superclasses]

  // Find the ceiling: the ontology's own answer where it has one, and
  // otherwise the first (most specific) fixed ceiling type in the ancestor
  // chain.
  let ceiling = null
  if (typeof ontology.boundaryOf === 'function') {
    ceiling = ontology.boundaryOf(currentType) || null
  } else {
    for (const type of typeChain) {
      if (CEILING_TYPE_IRIS.some((ceilingIri) => type.value === ceilingIri)) {
        ceiling = type
        break
      }
    }
  }

  // If no fixed ceiling found, fall back to current type
  if (!ceiling) {
    ceiling = currentType
  }

  // Get all subclasses of the ceiling as the hierarchy
  const ceilingSubclasses = ontology.subclasses(ceiling) || []
  const hierarchyTypes = [ceiling, ...ceilingSubclasses]

  // All types in hierarchy are compatible except current type
  const compatibleTypes = hierarchyTypes.filter((candidateType) => {
    return !candidateType.equals(currentType)
  })

  return {
    root: ceiling, // The ceiling is the effective root for type changes
    currentType,
    compatibleTypes,
    hierarchyTypes,
    outgoingEdges,
    incomingEdges,
    hasCompatibleTypes: compatibleTypes.length > 0,
  }
}

/**
 * Build a tree structure for the type hierarchy, showing only compatible types.
 * Incompatible types are pruned from the tree (not shown), except for
 * ancestor nodes needed to maintain the tree structure.
 *
 * @param {Object} compatibilityResult - Result from getCompatibleTypesInHierarchy
 * @param {Ontology} ontology - The ontology service
 * @returns {Array} - Tree nodes for v-treeview
 */
export function buildCompatibleTypeTree(compatibilityResult, ontology) {
  const { root, currentType, compatibleTypes } = compatibilityResult

  if (!root) return []

  // Get all classes sorted alphabetically
  const allClasses = ontology.classes.sort((a, b) =>
    a.type.short.localeCompare(b.type.short)
  )

  // Filter to only classes in this hierarchy
  const hierarchyClasses = allClasses.filter(
    (c) =>
      c.type.equals(root) ||
      (ontology.superclasses(c.type) || []).some((sc) => sc && sc.equals(root))
  )

  // Check if a type or any of its descendants is compatible or current
  function hasCompatibleDescendant(type) {
    const isCurrent = currentType.equals(type)
    const isCompatible = compatibleTypes.some((ct) => ct.equals(type))

    if (isCurrent || isCompatible) return true

    // Check children
    const children = hierarchyClasses.filter((c) => c.hasParent(type))
    return children.some((child) => hasCompatibleDescendant(child.type))
  }

  // Recursive utility to build pruned tree
  // Only includes nodes that are current, compatible, or have compatible descendants
  function buildNode(parentType) {
    const isCompatible = compatibleTypes.some((ct) => ct.equals(parentType))
    const isCurrent = currentType.equals(parentType)

    // Find children that should be included (have compatible descendants)
    const children = hierarchyClasses.filter((c) => c.hasParent(parentType))
    const includedChildren = children.filter((child) =>
      hasCompatibleDescendant(child.type)
    )

    // Only include this node if it's relevant (current, compatible, or has relevant children)
    if (!isCurrent && !isCompatible && includedChildren.length === 0) {
      return null
    }

    const node = {
      id: parentType.value,
      name: parentType.short + (isCurrent ? ' (current)' : ''),
      type: parentType,
      isCompatible,
      isCurrent,
      // Disabled for visual styling only (not used by item-disabled to avoid cascade)
      // Current type and structural-only parents are visually greyed out
      disabled: isCurrent || (!isCompatible && includedChildren.length > 0),
    }

    if (includedChildren.length > 0) {
      node.children = includedChildren
        .map((child) => buildNode(child.type))
        .filter((n) => n !== null)
    }

    return node
  }

  // Build tree starting from root
  // When root IS the current type, skip it and show children as top-level nodes
  // This avoids a disabled root node causing visual dimming of the entire tree
  if (root.equals(currentType)) {
    const directChildren = hierarchyClasses.filter((c) => c.hasParent(root))
    return directChildren
      .filter((child) => hasCompatibleDescendant(child.type))
      .map((child) => buildNode(child.type))
      .filter((n) => n !== null)
  }

  const rootNode = buildNode(root)
  return rootNode ? [rootNode] : []
}

/**
 * Get constraint violation details for a candidate type.
 * Useful for explaining why a type is incompatible.
 *
 * @param {RecIndividual} individual - The individual
 * @param {Iri} candidateType - The candidate type to check
 * @param {RecCache} cache - The cache
 * @param {Ontology} ontology - The ontology
 * @param {ConstraintsDB} constraintsDB - The constraints
 * @returns {Array} - Array of violation descriptions
 */
export function getConstraintViolations(
  individual,
  candidateType,
  cache,
  ontology,
  constraintsDB
) {
  const violations = []

  const candidateSuperclasses = ontology.superclasses(candidateType) || []
  const candidateChain = [candidateType, ...candidateSuperclasses]

  // Check outgoing relationships
  const outgoingEdges = cache.queryEdges({ source: individual.id.value })
  outgoingEdges.forEach((edge) => {
    const constraint = constraintsDB.constraints.find((c) =>
      c.relationship.equals(edge.label)
    )
    if (constraint) {
      const isValid = constraint.sources.some((source) =>
        candidateChain.some((t) => t.equals(source))
      )
      if (!isValid) {
        violations.push({
          type: 'outgoing',
          relationship: edge.label.short,
          target: edge.target.short || edge.target.value,
          requiredSources: constraint.sources.map((s) => s.short),
        })
      }
    }
  })

  // Check incoming relationships
  const incomingEdges = cache.queryEdges({ target: individual.id.value })
  incomingEdges.forEach((edge) => {
    const constraint = constraintsDB.constraints.find((c) =>
      c.relationship.equals(edge.label)
    )
    if (constraint) {
      const isValid = constraint.targets.some((target) =>
        candidateChain.some((t) => t.equals(target))
      )
      if (!isValid) {
        violations.push({
          type: 'incoming',
          relationship: edge.label.short,
          source: edge.source.short || edge.source.value,
          requiredTargets: constraint.targets.map((t) => t.short),
        })
      }
    }
  })

  return violations
}
